import type {
  DroneTelemetry,
  DroneTransport,
  FlightMode,
  GpsFixType,
  MissionState,
} from './types';
import { clamp, safeBoolean, safeNumber, safeString, wrapAngleDeg } from './math';
import { haversineMeters, isValidLatLng, normalizeLatitude, normalizeLongitude } from './geo';
import type { LatLng } from './geo';

/** 规范字段路径 → 原始报文中的来源路径。未列出的字段默认同名同路径。 */
export type FieldMap = Record<string, string>;

/* ------------------------------------------------------------------ *
 * 路径读取
 * ------------------------------------------------------------------ */

/** 按 `a.b.c` 逐级读取，任一层缺失返回 undefined。同时支持数组下标。 */
export function getPath(source: unknown, path: string): unknown {
  if (source === null || source === undefined || path === '') return undefined;
  let cursor: unknown = source;
  for (const segment of path.split('.')) {
    if (cursor === null || cursor === undefined) return undefined;
    if (Array.isArray(cursor)) {
      const index = Number(segment);
      if (!Number.isInteger(index)) return undefined;
      cursor = cursor[index];
      continue;
    }
    if (typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return cursor;
}

/** 先查 fieldMap 覆盖，再落到同名路径。 */
function readRaw(raw: unknown, canonicalPath: string, fieldMap: FieldMap): unknown {
  const mapped = fieldMap[canonicalPath];
  const value = getPath(raw, mapped ?? canonicalPath);
  if (value !== undefined) return value;
  // 映射写错时回退到同名路径，避免整帧数据因一个配置错误而全空
  return mapped ? getPath(raw, canonicalPath) : undefined;
}

function readNumber(raw: unknown, path: string, fieldMap: FieldMap, fallback: number): number {
  return safeNumber(readRaw(raw, path, fieldMap), fallback);
}

function readBoolean(raw: unknown, path: string, fieldMap: FieldMap, fallback: boolean): boolean {
  return safeBoolean(readRaw(raw, path, fieldMap), fallback);
}

function readString(raw: unknown, path: string, fieldMap: FieldMap, fallback: string): string {
  const value = safeString(readRaw(raw, path, fieldMap), fallback);
  return value === '' ? fallback : value;
}

function readEnum<T extends string>(
  raw: unknown,
  path: string,
  fieldMap: FieldMap,
  allowed: readonly T[],
  fallback: T,
): { value: T; issue: string | null } {
  const value = readRaw(raw, path, fieldMap);

  // 字段缺失时静默回退，与其余读取器保持一致（缺失由「完整数据列表」的空值提示体现）
  if (value === undefined || value === null || value === '') return { value: fallback, issue: null };

  if (typeof value === 'string') {
    const candidate = value.trim();
    if ((allowed as readonly string[]).includes(candidate)) return { value: candidate as T, issue: null };
  }

  // 兼容数字枚举（部分飞控用整数表示模式），同时接受 "2" 这类字符串化下标
  const numeric = safeNumber(value, Number.NaN);
  if (Number.isInteger(numeric) && numeric >= 0 && numeric < allowed.length) {
    const mapped = allowed[numeric];
    if (mapped !== undefined) return { value: mapped, issue: null };
  }

  return {
    value: fallback,
    issue: `${path}: 无法识别的取值「${describeRawValue(value)}」，已回退为「${fallback}」`,
  };
}

/** 把原始取值渲染成可读文本，用于 `issues` 提示。 */
function describeRawValue(value: unknown): string {
  if (typeof value === 'string') return value.length > 40 ? `${value.slice(0, 40)}…` : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return `[${value.length} 项数组]`;
  if (value !== null && typeof value === 'object') return '{对象}';
  return String(value);
}

function readNumberArray(raw: unknown, path: string, fieldMap: FieldMap): number[] {
  const value = readRaw(raw, path, fieldMap);
  if (!Array.isArray(value)) return [];
  const result: number[] = [];
  for (const entry of value) {
    const parsed = safeNumber(entry, Number.NaN);
    if (Number.isFinite(parsed)) result.push(parsed);
  }
  return result;
}

/* ------------------------------------------------------------------ *
 * 枚举取值
 * ------------------------------------------------------------------ */

export const GPS_FIX_TYPES: readonly GpsFixType[] = ['none', '2d', '3d', 'rtk-float', 'rtk-fixed'];
export const FLIGHT_MODES: readonly FlightMode[] = [
  'manual',
  'stabilize',
  'loiter',
  'auto',
  'rtl',
  'land',
  'offboard',
];
export const MISSION_STATES: readonly MissionState[] = [
  'idle',
  'uploading',
  'ready',
  'executing',
  'paused',
  'completed',
  'failed',
];

/** 规范字段全集。前端「完整数据列表」按此渲染，后端按此提供映射。 */
export const CANONICAL_FIELDS: readonly { path: string; label: string; unit: string; group: string }[] = [
  { path: 'seq', label: '帧序号', unit: '', group: '链路' },
  { path: 'timestamp', label: '无人机时间戳', unit: 'ms', group: '链路' },

  { path: 'attitude.rollDeg', label: '横滚角', unit: '°', group: '姿态' },
  { path: 'attitude.pitchDeg', label: '俯仰角', unit: '°', group: '姿态' },
  { path: 'attitude.yawDeg', label: '航向角', unit: '°', group: '姿态' },
  { path: 'attitude.rollRateDegS', label: '横滚角速度', unit: '°/s', group: '姿态' },
  { path: 'attitude.pitchRateDegS', label: '俯仰角速度', unit: '°/s', group: '姿态' },
  { path: 'attitude.yawRateDegS', label: '航向角速度', unit: '°/s', group: '姿态' },

  { path: 'position.lat', label: '纬度', unit: '°', group: '位置' },
  { path: 'position.lng', label: '经度', unit: '°', group: '位置' },
  { path: 'position.altM', label: '相对高度', unit: 'm', group: '位置' },
  { path: 'position.altAbsM', label: '海拔高度', unit: 'm', group: '位置' },
  { path: 'position.homeDistanceM', label: '距起飞点', unit: 'm', group: '位置' },

  { path: 'velocity.groundSpeedMs', label: '地速', unit: 'm/s', group: '速度' },
  { path: 'velocity.verticalSpeedMs', label: '垂直速度', unit: 'm/s', group: '速度' },
  { path: 'velocity.northMs', label: '北向速度', unit: 'm/s', group: '速度' },
  { path: 'velocity.eastMs', label: '东向速度', unit: 'm/s', group: '速度' },

  { path: 'battery.percent', label: '电池电量', unit: '%', group: '电源' },
  { path: 'battery.voltageV', label: '总电压', unit: 'V', group: '电源' },
  { path: 'battery.currentA', label: '电流', unit: 'A', group: '电源' },
  { path: 'battery.cellCount', label: '电芯数', unit: 'S', group: '电源' },
  { path: 'battery.temperatureC', label: '电池温度', unit: '°C', group: '电源' },
  { path: 'battery.remainingSec', label: '剩余续航', unit: 's', group: '电源' },

  { path: 'gps.fixType', label: '定位类型', unit: '', group: '定位' },
  { path: 'gps.satellites', label: '可见卫星', unit: '颗', group: '定位' },
  { path: 'gps.hdop', label: 'HDOP', unit: '', group: '定位' },
  { path: 'gps.vdop', label: 'VDOP', unit: '', group: '定位' },

  { path: 'opticalFlow.quality', label: '光流质量', unit: '', group: '传感器' },
  { path: 'opticalFlow.enabled', label: '光流启用', unit: '', group: '传感器' },
  { path: 'opticalFlow.heightM', label: '光流高度', unit: 'm', group: '传感器' },

  { path: 'link.rssiDbm', label: '信号强度', unit: 'dBm', group: '链路' },
  { path: 'link.quality', label: '信号质量', unit: '', group: '链路' },
  { path: 'link.latencyMs', label: '链路延迟', unit: 'ms', group: '链路' },
  { path: 'link.packetLossPct', label: '丢包率', unit: '%', group: '链路' },

  { path: 'flight.armed', label: '电机解锁', unit: '', group: '飞控' },
  { path: 'flight.inAir', label: '飞行中', unit: '', group: '飞控' },
  { path: 'flight.mode', label: '飞行模式', unit: '', group: '飞控' },
  { path: 'flight.missionState', label: '航线任务状态', unit: '', group: '飞控' },
  { path: 'flight.missionCurrentWaypoint', label: '当前航点', unit: '', group: '飞控' },
  { path: 'flight.missionTotalWaypoints', label: '航点总数', unit: '', group: '飞控' },

  { path: 'gimbal.pitchDeg', label: '云台俯仰', unit: '°', group: '云台' },
  { path: 'gimbal.yawDeg', label: '云台航向', unit: '°', group: '云台' },
  { path: 'gimbal.rollDeg', label: '云台横滚', unit: '°', group: '云台' },

  { path: 'environment.temperatureC', label: '环境温度', unit: '°C', group: '环境' },
  { path: 'environment.windSpeedMs', label: '风速', unit: 'm/s', group: '环境' },
  { path: 'environment.windDirectionDeg', label: '风向', unit: '°', group: '环境' },
  { path: 'environment.pressureHpa', label: '气压', unit: 'hPa', group: '环境' },
];

/* ------------------------------------------------------------------ *
 * 解析入口
 * ------------------------------------------------------------------ */

export interface TelemetryParseContext {
  droneId: string;
  fieldMap?: FieldMap;
  /** 前端接收时间，默认取当前时间 */
  receivedAt?: number;
  /** 实际使用的传输方式，由前端 Hub 标注 */
  transport?: DroneTransport;
  /** 起飞点，用于在报文缺失时补算「距起飞点」 */
  home?: LatLng | null;
}

export interface TelemetryParseResult {
  telemetry: DroneTelemetry;
  /** 非致命问题，用于在「完整数据列表」里提示字段异常 */
  issues: string[];
}

/**
 * 把任意无人机原始报文映射为规范化遥测帧。
 *
 * 设计要点：
 * - 永不抛错。实时链路里一帧坏数据不应中断整条流。
 * - 缺字段回退为安全默认值，并在 `issues` 中记录，便于排查对接问题。
 * - 通过 `fieldMap` 支持不同厂商的字段命名，无需改代码。
 */
export function parseTelemetry(raw: unknown, ctx: TelemetryParseContext): TelemetryParseResult {
  const issues: string[] = [];
  const fieldMap: FieldMap = ctx.fieldMap ?? {};
  const receivedAt = ctx.receivedAt ?? Date.now();
  const source = raw !== null && typeof raw === 'object' ? raw : {};
  if (raw !== null && typeof raw !== 'object') {
    issues.push('报文不是对象，已按空帧处理');
  }

  const timestamp = readNumber(source, 'timestamp', fieldMap, receivedAt);
  const seq = readNumber(source, 'seq', fieldMap, 0);

  const rollDeg = readNumber(source, 'attitude.rollDeg', fieldMap, 0);
  const pitchDeg = readNumber(source, 'attitude.pitchDeg', fieldMap, 0);
  const yawDeg = wrapAngleDeg(readNumber(source, 'attitude.yawDeg', fieldMap, 0));

  let lat = normalizeLatitude(readNumber(source, 'position.lat', fieldMap, 0));
  let lng = normalizeLongitude(readNumber(source, 'position.lng', fieldMap, 0));
  if (!isValidLatLng(lat, lng)) {
    issues.push('position.lat/lng 超出合法范围，已置零');
    lat = 0;
    lng = 0;
  }

  const cellCount = Math.max(0, Math.round(readNumber(source, 'battery.cellCount', fieldMap, 0)));
  const voltageV = readNumber(source, 'battery.voltageV', fieldMap, 0);
  let cellVoltagesV = readNumberArray(source, 'battery.cellVoltagesV', fieldMap);
  if (cellVoltagesV.length === 0 && cellCount > 0 && voltageV > 0) {
    // 报文未提供单体电压时按总电压均分，避免功率相关告警全部失效
    cellVoltagesV = Array.from({ length: cellCount }, () => voltageV / cellCount);
  }

  const gpsFix = readEnum(source, 'gps.fixType', fieldMap, GPS_FIX_TYPES, 'none');
  if (gpsFix.issue) issues.push(gpsFix.issue);
  const flightMode = readEnum(source, 'flight.mode', fieldMap, FLIGHT_MODES, 'manual');
  if (flightMode.issue) issues.push(flightMode.issue);
  const missionState = readEnum(source, 'flight.missionState', fieldMap, MISSION_STATES, 'idle');
  if (missionState.issue) issues.push(missionState.issue);

  let homeDistanceM = readNumber(source, 'position.homeDistanceM', fieldMap, Number.NaN);
  if (!Number.isFinite(homeDistanceM)) {
    homeDistanceM = ctx.home ? haversineMeters(ctx.home, { lat, lng }) : 0;
  }

  const telemetry: DroneTelemetry = {
    droneId: readString(source, 'droneId', fieldMap, ctx.droneId),
    seq: Math.max(0, Math.round(seq)),
    timestamp,
    receivedAt,
    attitude: {
      rollDeg: clamp(rollDeg, -180, 180),
      pitchDeg: clamp(pitchDeg, -180, 180),
      yawDeg,
      rollRateDegS: readNumber(source, 'attitude.rollRateDegS', fieldMap, 0),
      pitchRateDegS: readNumber(source, 'attitude.pitchRateDegS', fieldMap, 0),
      yawRateDegS: readNumber(source, 'attitude.yawRateDegS', fieldMap, 0),
    },
    position: {
      lat,
      lng,
      altM: readNumber(source, 'position.altM', fieldMap, 0),
      altAbsM: readNumber(source, 'position.altAbsM', fieldMap, 0),
      homeDistanceM,
    },
    velocity: {
      groundSpeedMs: readNumber(source, 'velocity.groundSpeedMs', fieldMap, 0),
      verticalSpeedMs: readNumber(source, 'velocity.verticalSpeedMs', fieldMap, 0),
      northMs: readNumber(source, 'velocity.northMs', fieldMap, 0),
      eastMs: readNumber(source, 'velocity.eastMs', fieldMap, 0),
    },
    battery: {
      percent: clamp(readNumber(source, 'battery.percent', fieldMap, 0), 0, 100),
      voltageV,
      currentA: readNumber(source, 'battery.currentA', fieldMap, 0),
      cellCount: cellCount > 0 ? cellCount : cellVoltagesV.length,
      cellVoltagesV,
      temperatureC: readNumber(source, 'battery.temperatureC', fieldMap, 0),
      remainingSec: Math.max(0, readNumber(source, 'battery.remainingSec', fieldMap, 0)),
    },
    gps: {
      fixType: gpsFix.value,
      satellites: Math.max(0, Math.round(readNumber(source, 'gps.satellites', fieldMap, 0))),
      hdop: Math.max(0, readNumber(source, 'gps.hdop', fieldMap, 99)),
      vdop: Math.max(0, readNumber(source, 'gps.vdop', fieldMap, 99)),
    },
    opticalFlow: {
      quality: clamp(readNumber(source, 'opticalFlow.quality', fieldMap, 0), 0, 100),
      enabled: readBoolean(source, 'opticalFlow.enabled', fieldMap, false),
      heightM: readNumber(source, 'opticalFlow.heightM', fieldMap, 0),
    },
    link: {
      rssiDbm: readNumber(source, 'link.rssiDbm', fieldMap, -120),
      quality: clamp(readNumber(source, 'link.quality', fieldMap, 0), 0, 100),
      latencyMs: Math.max(0, readNumber(source, 'link.latencyMs', fieldMap, 0)),
      packetLossPct: clamp(readNumber(source, 'link.packetLossPct', fieldMap, 0), 0, 100),
      transport: ctx.transport ?? 'ws',
    },
    flight: {
      armed: readBoolean(source, 'flight.armed', fieldMap, false),
      inAir: readBoolean(source, 'flight.inAir', fieldMap, false),
      mode: flightMode.value,
      missionState: missionState.value,
      missionId: readString(source, 'flight.missionId', fieldMap, '') || null,
      missionCurrentWaypoint: Math.max(
        0,
        Math.round(readNumber(source, 'flight.missionCurrentWaypoint', fieldMap, 0)),
      ),
      missionTotalWaypoints: Math.max(
        0,
        Math.round(readNumber(source, 'flight.missionTotalWaypoints', fieldMap, 0)),
      ),
    },
    gimbal: {
      pitchDeg: readNumber(source, 'gimbal.pitchDeg', fieldMap, 0),
      yawDeg: wrapAngleDeg(readNumber(source, 'gimbal.yawDeg', fieldMap, 0)),
      rollDeg: readNumber(source, 'gimbal.rollDeg', fieldMap, 0),
    },
    environment: {
      temperatureC: readNumber(source, 'environment.temperatureC', fieldMap, 0),
      windSpeedMs: readNumber(source, 'environment.windSpeedMs', fieldMap, 0),
      windDirectionDeg: wrapAngleDeg(readNumber(source, 'environment.windDirectionDeg', fieldMap, 0)),
      pressureHpa: readNumber(source, 'environment.pressureHpa', fieldMap, 0),
    },
    raw: source as Record<string, unknown>,
  };

  return { telemetry, issues };
}

/**
 * 从任意响应体中提取遥测对象。
 * 兼容 `{data:{...}}` / `{telemetry:{...}}` / `{payload:{...}}` 三种常见包裹形式。
 */
export function unwrapTelemetryPayload(body: unknown): unknown {
  if (body === null || typeof body !== 'object') return body;
  const record = body as Record<string, unknown>;
  for (const key of ['data', 'telemetry', 'payload', 'result']) {
    const nested = record[key];
    if (nested !== null && typeof nested === 'object' && !Array.isArray(nested)) return nested;
  }
  return body;
}

/** 取一组遥测帧中最新的接收时间。 */
export function latestReceivedAt(frames: readonly DroneTelemetry[]): number | null {
  let latest: number | null = null;
  for (const frame of frames) {
    if (latest === null || frame.receivedAt > latest) latest = frame.receivedAt;
  }
  return latest;
}
